import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import * as Mutation from '../../../../api/gql/mutation'
import { useRouter } from '../../../../hooks'
import type { DependencyType } from '../../lib/constants'
import { linkEndpointLabel } from '../../lib/link-labels'
import type { ScheduleActivity, ScheduleDependency, ScheduleExternalActivity } from '../../types'
import ConfirmDialog from '../../components/ConfirmDialog'
import { useGanttOps } from '../gantt-ops'
import AddLinkRow from './AddLinkRow'
import EdgeRow, { type EdgeRowData } from './EdgeRow'

interface Props {
  activity: ScheduleActivity
  byId: Map<string, ScheduleActivity>
  externalById: Map<string, ScheduleExternalActivity>
  dependencies: ScheduleDependency[]
  scheduleName: string
  projectId: string
  scheduleId: string
  canEdit: boolean
}

/**
 * Predecessors/successors of the activity panel (Task 17, spec B §9.7 step 5).
 *
 * Reads edges straight off `detail.dependencies` rather than `GanttOps.predecessorsByActivity` /
 * `successorsByActivity`: those maps are shaped for the grid's read-only Predecessors column, and
 * `successorsByActivity`'s local branch only carries the successor's activity id — no dependency
 * id/type/lag, which this section's editable Type/Lag controls and delete button need on *both*
 * sides. Filtering `detail.dependencies` by `predecessorId`/`successorId` gives full, symmetric data
 * for both lists at the same cost (one pass over the dependency array).
 *
 * `EdgeRow` and `AddLinkRow` live in their own files (fix round 1, review finding #8 — this file
 * used to hold both plus this component, at 268 lines).
 */
const DependenciesSection: React.FC<Props> = ({ activity, byId, externalById, dependencies, scheduleName, projectId, scheduleId, canEdit }) => {
  const { t } = useTranslation()
  const { history } = useRouter()
  const { run, focusActivity, rowNumberById, idByRowNumber } = useGanttOps()
  const [updateDependency] = useMutation(Mutation.UPDATE_SCHEDULE_DEPENDENCY)
  const [deleteDependency] = useMutation(Mutation.DELETE_SCHEDULE_DEPENDENCY)
  const [createDependency] = useMutation(Mutation.CREATE_SCHEDULE_DEPENDENCY)
  const [pendingId, setPendingId] = useState<string | null>(null)
  const [confirmTarget, setConfirmTarget] = useState<{ id: string; from: string; to: string } | null>(null)
  const [deleting, setDeleting] = useState(false)

  // Freelo #32253582: resolves a bare row number typed into `AddLinkRow`'s picker to a picker
  // result, entirely from data already in memory — no server round trip.
  const resolveRow = useCallback((n: number): ScheduleExternalActivity | null => {
    const id = idByRowNumber.get(n)
    const a = id ? byId.get(id) : undefined
    return a ? { id: a.id, scheduleId, scheduleName, name: a.name, wbsCode: a.wbsCode, start: a.start, finish: a.finish, isMilestone: a.isMilestone } : null
  }, [idByRowNumber, byId, scheduleId, scheduleName])

  const toRow = (d: ScheduleDependency, peerId: string): EdgeRowData => {
    const peer = linkEndpointLabel(byId, externalById, rowNumberById, peerId)
    return { depId: d.id, peerId, peerScheduleId: peer.scheduleId, peerLabel: peer.label, isForeign: peer.foreign, type: d.type, lagDays: d.lagDays }
  }
  const preds = useMemo(
    () => dependencies.filter((d) => d.successorId === activity.id).map((d) => toRow(d, d.predecessorId)),
    [dependencies, activity.id, byId, externalById, rowNumberById],
  )
  const succs = useMemo(
    () => dependencies.filter((d) => d.predecessorId === activity.id).map((d) => toRow(d, d.successorId)),
    [dependencies, activity.id, byId, externalById, rowNumberById],
  )
  const selfLabel = `${rowNumberById.get(activity.id) ?? activity.wbsCode} ${activity.name}`

  const navigate = (row: EdgeRowData) => {
    if (row.isForeign) history.push(`/project/${projectId}/schedule/${row.peerScheduleId}?activity=${row.peerId}`)
    else focusActivity(row.peerId)
  }

  const changeType = async (row: EdgeRowData, type: DependencyType) => {
    setPendingId(row.depId)
    try {
      await run(() => updateDependency({ variables: { scheduleId, id: row.depId, type } }) as unknown as Promise<{ data: any }>, (d: any) => d.updateScheduleDependency)
    } catch { /* run() already toasted */ } finally { setPendingId(null) }
  }
  const changeLag = async (row: EdgeRowData, lagDays: number) => {
    setPendingId(row.depId)
    try {
      await run(() => updateDependency({ variables: { scheduleId, id: row.depId, lagDays } }) as unknown as Promise<{ data: any }>, (d: any) => d.updateScheduleDependency)
    } catch { /* run() already toasted */ } finally { setPendingId(null) }
  }
  const askDelete = (row: EdgeRowData, direction: 'pred' | 'succ') => {
    setConfirmTarget(direction === 'pred' ? { id: row.depId, from: row.peerLabel, to: selfLabel } : { id: row.depId, from: selfLabel, to: row.peerLabel })
  }
  const confirmDelete = async () => {
    if (!confirmTarget) return
    setDeleting(true)
    try {
      await run(() => deleteDependency({ variables: { scheduleId, id: confirmTarget.id } }) as unknown as Promise<{ data: any }>, (d: any) => d.deleteScheduleDependency)
      setConfirmTarget(null)
    } catch { /* run() already toasted */ } finally { setDeleting(false) }
  }

  // `selfId` is threaded through explicitly (not only closed over) so the handler always links
  // against the activity that was on screen when `AddLinkRow`'s Add button was clicked — belt and
  // suspenders alongside `key={activity.id}` remounting `AddLinkRow` on focus change (fix round 1,
  // review finding #1).
  const addLink = (direction: 'pred' | 'succ', selfId: string, target: ScheduleExternalActivity, type: DependencyType, lagDays: number) => {
    const input = direction === 'pred'
      ? { predecessorId: target.id, successorId: selfId, type, lagDays }
      : { predecessorId: selfId, successorId: target.id, type, lagDays }
    return run(() => createDependency({ variables: { scheduleId, input } }) as unknown as Promise<{ data: any }>, (d: any) => d.createScheduleDependency)
  }

  return (
    <div className="space-y-3">
      <div>
        <div className="text-xs font-medium text-muted-foreground mb-1">{t('Schedule_predecessors')}</div>
        <div>
          {preds.map((row) => (
            <EdgeRow
              key={row.depId} row={row} canEdit={canEdit} pending={pendingId === row.depId}
              onNavigate={() => navigate(row)} onChangeType={(ty) => changeType(row, ty)} onChangeLag={(n) => changeLag(row, n)}
              onDelete={() => askDelete(row, 'pred')}
            />
          ))}
        </div>
        {canEdit && (
          <AddLinkRow
            key={activity.id}
            direction="pred" projectId={projectId} scheduleId={scheduleId} scheduleName={scheduleName}
            activityId={activity.id} rowNumberById={rowNumberById} resolveRow={resolveRow}
            onAdd={(selfId, target, type, lag) => addLink('pred', selfId, target, type, lag)}
          />
        )}
      </div>
      <div>
        <div className="text-xs font-medium text-muted-foreground mb-1">{t('Schedule_successors')}</div>
        <div>
          {succs.map((row) => (
            <EdgeRow
              key={row.depId} row={row} canEdit={canEdit} pending={pendingId === row.depId}
              onNavigate={() => navigate(row)} onChangeType={(ty) => changeType(row, ty)} onChangeLag={(n) => changeLag(row, n)}
              onDelete={() => askDelete(row, 'succ')}
            />
          ))}
        </div>
        {canEdit && (
          <AddLinkRow
            key={activity.id}
            direction="succ" projectId={projectId} scheduleId={scheduleId} scheduleName={scheduleName}
            activityId={activity.id} rowNumberById={rowNumberById} resolveRow={resolveRow}
            onAdd={(selfId, target, type, lag) => addLink('succ', selfId, target, type, lag)}
          />
        )}
      </div>
      <ConfirmDialog
        open={!!confirmTarget}
        title={t('Schedule_link_delete_title')}
        description={confirmTarget ? t('Schedule_link_delete_body', { from: confirmTarget.from, to: confirmTarget.to }) : undefined}
        confirmLabel={t('Common_delete')}
        variant="destructive"
        submitting={deleting}
        onCancel={() => setConfirmTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  )
}

export default DependenciesSection
